import axios from "axios";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import { useRecoilState } from "recoil";
import { getViewAsCompanyValue } from "src/components/utilities";
import {
  DEFAULT_HEADERS,
  DEFAULT_SYSADMIN_HEADERS,
  PASSINGS_HEADERS,
  ROLES,
  ROLES_LEVEL,
} from "src/constants";
import { userStore } from "src/store";
import useSWR from "swr";

const DEFAULT_COMPANY_SETTINGS = {
  passingsPaginationLimit: 24,
  excludeLabels: [],
};

export function useUser({ redirectTo, redirectIfFound, restrictToRole } = {}) {
  const viewAsCompany = getViewAsCompanyValue();
  const [userStoreData, setUserStoreData] = useRecoilState(userStore);
  const router = useRouter();

  const { data, error, isLoading } = useSWR(
    "/api/auth/user",
    (url) =>
      axios
        .get(url, { params: viewAsCompany })
        .then((response) => response.data),
    {
      revalidateOnFocus: false,
      onSuccess: (data) => setUserStoreData(data),
      onError: (err) => {
        // token is removed in endpoint
        console.error(err);
        localStorage.removeItem("dts-should-redirect");
        localStorage.removeItem("viewAsCompany");
        router.push("/");
      },
    }
  );

  const hasUser = Boolean(data && Object.keys(data).length !== 0);

  useEffect(() => {
    if (restrictToRole && data) {
      if (ROLES_LEVEL[data?.role] < ROLES_LEVEL[restrictToRole]) {
        router.push(redirectTo || "/dashboard");
        return;
      }
    }

    if (!redirectTo || isLoading || !Boolean(data)) return;

    if (redirectTo && !redirectIfFound && !hasUser) {
      localStorage.removeItem("dts-should-redirect");
      router.push(redirectTo);
    } else if (redirectIfFound && hasUser) {
      router.push(redirectTo);
    }
  }, [data, isLoading]);

  const headersFromCompany = useMemo(() => {
    const headers = data?.Company?.settings?.passingsTableHeaders;

    if (ROLES_LEVEL[data?.role] < ROLES_LEVEL.admin && Array.isArray(headers)) {
      return headers?.filter?.(
        (header) => header !== PASSINGS_HEADERS.CORRECTED
      );
    }

    const isSysAdminOrAbove =
      ROLES_LEVEL[data?.role || ROLES.inspector] >= ROLES_LEVEL.sysAdmin;

    if (Array.isArray(headers)) return headers;
    else return isSysAdminOrAbove ? DEFAULT_SYSADMIN_HEADERS : DEFAULT_HEADERS;
  }, [data]);

  if (error) return {};

  return {
    ...data,
    companySettings: {
      passingsTableHeaders: headersFromCompany,
      ...DEFAULT_COMPANY_SETTINGS,
      ...data?.Company?.settings,
    },
    hasUser: hasUser,
    roleLevel: ROLES_LEVEL[data?.role],
  };
}
